import { useMemo, useState } from "react";
import { useAudit } from "../contexts/auditContext";
import { useAuth } from "../contexts/authContext";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import {
  AuditSession,
  SourceRow,
  type ProcedureSessionDocumentsResponse,
} from "../models/audit/auditSession";
import { useHookCache } from "../lib/hookCache";
import { useSourceRow } from "./audit/useSourceRow";
import { useProcedureSessionDocuments } from "./audit/useProcedureSessionDocuments";
import { useProcedureSessionTableShell } from "./audit/useProcedureSessionTableShell";

type SessionDocument = ProcedureSessionDocumentsResponse["documents"][number];

export const useDocumentManagement = (
  procedureSessionId: string,
  _controlSessionId: string,
  auditSourceRowId: string,
) => {
  const { updateCurrentDocument } = useAudit();
  const { setEntry, invalidate } = useHookCache();
  const { user } = useAuth();

  const { data: currentSourceRow } = useSourceRow(
    procedureSessionId,
    auditSourceRowId,
  );
  const { data: documentsResponse } =
    useProcedureSessionDocuments(procedureSessionId);
  const { data: tableShell } =
    useProcedureSessionTableShell(procedureSessionId);

  const procedure = tableShell?.procedure;

  const [localSelectedDocument, setLocalSelectedDocument] = useState<{
    documentId: string;
    type: string;
    url?: string;
    fileType: string;
  } | null>(null);

  const documentIdToDocumentMap = useMemo(() => {
    return (documentsResponse?.documents ?? []).reduce(
      (acc, doc) => {
        const key = doc.documentId ?? doc.id;
        acc[key] = doc;
        return acc;
      },
      {} as Record<string, SessionDocument>,
    );
  }, [documentsResponse]);

  const getOrderedDocuments =
    procedure?.requiredDocuments
      .flatMap((proc) => {
        const docs = currentSourceRow?.matchItems.filter((d) => {
          const hasLegacyAssignment = d.assignments.some(
            (a) => a.procedureDocumentId === proc.id,
          );
          const docAssignments =
            documentIdToDocumentMap?.[d.sourceDocument.id]?.fetchableDocument
              ?.documentAssignments ?? [];
          const hasNewAssignment = docAssignments.some((a) => a.id === proc.id);
          return hasLegacyAssignment || hasNewAssignment;
        });
        return docs?.filter((doc) => doc.state !== "rejected") || [];
      })
      .filter(Boolean)
      .sort((a, b) => a.id.localeCompare(b.id)) || [];

  const updateDocumentStatus = async (
    documentId: string,
    newStatus: "approved" | "pending" | "rejected",
  ) => {
    const matchItemsToUpdate = currentSourceRow?.matchItems.filter(
      (item) => item.sourceDocument.id === documentId,
    );
    if (!matchItemsToUpdate || matchItemsToUpdate.length === 0) {
      toast.error("Failed to update document status");
      return;
    }

    setEntry<SourceRow>(
      [
        "procedure-session",
        procedureSessionId,
        "source-rows",
        auditSourceRowId,
      ],
      (prev) => ({
        ...prev,
        matchItems: prev.matchItems.map((item) =>
          item.sourceDocument.id === documentId
            ? { ...item, state: newStatus }
            : item,
        ),
      }),
    );

    try {
      await fetchFastApi({
        method: "POST",
        path: `/matching/match-session/${procedureSessionId}/source-row/${auditSourceRowId}/match-item/${matchItemsToUpdate[0].id}`,
        body: {
          new_status: newStatus,
        },
      });
      // Invalidate the slim list so documentCount refreshes if the state
      // change crossed the approved/non-approved boundary.
      await invalidate([
        "procedure-session",
        procedureSessionId,
        "source-rows",
      ]);
    } catch (error) {
      toast.error("Failed to update document status");
      console.error(error);
    }
  };

  const selectNextDocument = (currentDocId: string) => {
    const currentIndex = getOrderedDocuments.findIndex(
      (doc) => doc.sourceDocument.id === currentDocId,
    );

    if (currentIndex === -1) return;

    const nextDoc = getOrderedDocuments[currentIndex + 1];
    if (nextDoc) {
      if (nextDoc.state !== "pending") {
        setLocalSelectedDocument(null);
        updateCurrentDocument(null);
        return;
      }

      const nextDocData = {
        id: documentIdToDocumentMap?.[nextDoc.sourceDocument.id]?.id,
        documentId: nextDoc.sourceDocument.id,
        type: nextDoc.sourceDocument.fileType,
        fileType: nextDoc.sourceDocument.fileType,
        fileName: nextDoc.sourceDocument.fileName,
      };
      setLocalSelectedDocument(nextDocData);
      updateCurrentDocument({
        ...nextDocData,
        procedureSessionId,
      });
    } else {
      setLocalSelectedDocument(null);
      updateCurrentDocument(null);
    }
  };

  const removeDocument = async (documentId: string) => {
    const matchItemToDelete = currentSourceRow?.matchItems.find(
      (item) => item.sourceDocument.id === documentId,
    );

    if (!matchItemToDelete) {
      toast.error("Failed to find document to remove");
      return;
    }

    setEntry<SourceRow>(
      [
        "procedure-session",
        procedureSessionId,
        "source-rows",
        auditSourceRowId,
      ],
      (prev) => ({
        ...prev,
        matchItems: prev.matchItems.filter(
          (item) => item.sourceDocument.id !== documentId,
        ),
      }),
    );

    // The sample-list doc-count badge derives from the full session's
    // matchItems (legacy-rule recompute in AuditSampleList), so mirror the
    // removal there too — user-initiated deletion doesn't trigger a
    // documentsState→ready transition, so the polling-driven invalidation
    // in useControlSessionStatus won't refresh the badge on its own.
    setEntry<AuditSession>(
      ["procedure-session", procedureSessionId],
      (prev) => ({
        ...prev,
        sourceRows: prev.sourceRows.map((row) =>
          row.id !== auditSourceRowId
            ? row
            : {
                ...row,
                matchItems: row.matchItems.filter(
                  (item) => item.sourceDocument.id !== documentId,
                ),
              },
        ),
      }),
    );

    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/matching/match-session/${procedureSessionId}/source-row/${auditSourceRowId}/match-item/${matchItemToDelete.id}`,
      });
      await invalidate([
        "procedure-session",
        procedureSessionId,
        "source-rows",
      ]);
    } catch (error) {
      toast.error("Failed to remove document");
      console.error(error);
    }

    if (localSelectedDocument?.documentId === documentId) {
      setLocalSelectedDocument(null);
      updateCurrentDocument(null);
    }
  };

  const markProcedureAsNotRequired = async (procedureId: string) => {
    if (!user?.id) return;

    setEntry<SourceRow>(
      [
        "procedure-session",
        procedureSessionId,
        "source-rows",
        auditSourceRowId,
      ],
      (prev) => ({
        ...prev,
        requiredDocumentsActions: [
          ...prev.requiredDocumentsActions,
          {
            id: crypto.randomUUID(),
            sourceRowId: auditSourceRowId,
            procedureDocumentId: procedureId,
            action: "mark_missing",
            userId: user.id,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ],
      }),
    );

    try {
      await fetchFastApi({
        method: "POST",
        path: `/matching/match-session/${procedureSessionId}/source-row/${auditSourceRowId}/document-action`,
        body: {
          action: "mark_missing",
          procedureDocumentId: procedureId,
        },
      });
    } catch (error) {
      toast.error("Failed to mark procedure as not required");
      console.error(error);
    }
  };

  const markProcedureAsPending = async (procedureDocumentId: string) => {
    if (!user?.id) return;
    const sourceRowDocumentActionId =
      currentSourceRow?.requiredDocumentsActions.find(
        (action) => action.procedureDocumentId === procedureDocumentId,
      )?.id;

    setEntry<SourceRow>(
      [
        "procedure-session",
        procedureSessionId,
        "source-rows",
        auditSourceRowId,
      ],
      (prev) => ({
        ...prev,
        requiredDocumentsActions: prev.requiredDocumentsActions.filter(
          (action) => action.id !== sourceRowDocumentActionId,
        ),
      }),
    );

    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/matching/match-session/${procedureSessionId}/source-row/${auditSourceRowId}/document-action/${sourceRowDocumentActionId}`,
      });
    } catch (error) {
      toast.error("Failed to mark procedure as missing");
      console.error(error);
    }
  };

  return {
    documentIdToDocumentMap,
    currentSourceRow,
    localSelectedDocument,
    setLocalSelectedDocument,
    getOrderedDocuments,
    updateDocumentStatus,
    selectNextDocument,
    removeDocument,
    markProcedureAsNotRequired,
    markProcedureAsPending,
  };
};
